import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { actions } from "@containers/PayPerLead/store";
import { PayPerLeadSubscription } from "@containers/PayPerLead/interface";
import { actions as accountActions, selectors as accountSelectors } from "@containers/Account/store";
import { goToBillingPortal } from "@containers/Account/store/actions";

import "./index.scss";

interface UnpaidSubscriptionsProps {
  unpaidSubscriptions: PayPerLeadSubscription[];
}

export const UnpaidSubscriptions: React.FC<UnpaidSubscriptionsProps> = ({ unpaidSubscriptions }) => {
  const dispatch = useDispatch();
  const [totalPaymentAmount, setTotalPaymentAmount] = useState<number>(0);
  const hasPaymentMethod = useSelector(accountSelectors.selectPaymentMethodExistence());
  const hasCardPaymentMethod = useSelector(accountSelectors.selectCardPaymentMethodExistence());
  const account = useSelector(accountSelectors.getActiveAccount());
  const allowPayment = hasCardPaymentMethod || (account?.credits ?? 0) >= totalPaymentAmount;
  const reason = !hasPaymentMethod
    ? "Payment method is missing."
    : !hasCardPaymentMethod
    ? "Only card payment is allowed."
    : "";

  const hasLocked = unpaidSubscriptions.some((s) => s.lock);
  const hasRenew = unpaidSubscriptions.some((s) => !s.is_active);

  const billingPortalNavigate = () => {
    if (account) {
      dispatch(goToBillingPortal({ returnUrl: location.href }));
    }
  };

  const billingPortalLink = (
    <span onClick={billingPortalNavigate} className="billing-tab">
      billing tab
    </span>
  );

  const errorMessage = hasRenew
    ? "Your payment for renewal subscription has failed. "
    : `We will stop delivering leads as your Balance is at $0. ${reason} `;

  const hasFetchedTotalPayment = useRef(false);

  useEffect(() => {
    if (account?.id && !hasCardPaymentMethod) {
      dispatch(accountActions.checkPaymentMethodExistence.request({}));
    }
  }, [dispatch, account, hasCardPaymentMethod]);

  useEffect(() => {
    if (unpaidSubscriptions.length && !hasFetchedTotalPayment.current) {
      hasFetchedTotalPayment.current = true; // Mark as fetched
      dispatch(
        actions.fetchTotalPaymentAmount.request({
          ids: unpaidSubscriptions.map((subscription) => subscription.id as number),
          callback: (paymentAmount: number) => setTotalPaymentAmount(paymentAmount),
        }),
      );
    }
  }, [dispatch, unpaidSubscriptions]);

  if (!unpaidSubscriptions?.length) return null;

  const tryAgain = () => {
    if (account) {
      if (allowPayment) {
        dispatch(actions.purchaseSubscriptions.request(unpaidSubscriptions));
      } else {
        const successUrl = new URL(location.href);

        dispatch(
          accountActions.goToCheckout({
            successUrl: successUrl.toString(),
            cancelUrl: location.href,
          }),
        );
      }
    }
  };

  return (
    <div className="pay-per-lead-payment-failed">
      <div className="pay-per-lead-payment-failed-text">
        <b>Payment required</b>
        <div>
          {errorMessage}
          Please visit your {billingPortalLink} to update your payment method or manage your subscriptions
        </div>
      </div>
      {!hasLocked && (
        <div className="pay-per-lead-payment-failed-button" onClick={tryAgain}>
          Try again
        </div>
      )}
    </div>
  );
};
